/**
 * Cart updates stay on the current page.
 * Only an explicit cart click may open the cart or navigate to its page.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

let explicitCartClickUntil = 0;

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const trigger = event.target.closest('a, button, [role="button"]');
  if (!trigger) return;
  let cartLink = false;
  const href = trigger.getAttribute('href');
  if (href) {
    try {
      const url = new URL(href, window.location.origin);
      cartLink = url.origin === window.location.origin && /\/cart\/?$/.test(url.pathname);
    } catch (_) {}
  }
  const cartIcon = trigger.matches('.header-actions__cart-icon, .header-actions__cart') ||
    Boolean(trigger.querySelector('.header-actions__cart-icon')) ||
    Boolean(trigger.closest('cart-icon-component'));
  explicitCartClickUntil = cartLink || cartIcon ? Date.now() + 1200 : 0;
}, true);

function init() {
  document.querySelectorAll('cart-drawer-component[auto-open]').forEach(drawer => drawer.removeAttribute('auto-open'));
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      // Ignore automatic openCart calls after add-to-cart.
      if (Date.now() > explicitCartClickUntil) return;
      explicitCartClickUntil = 0;
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer) {
        if (typeof drawer.open !== 'function' && customElements.get('theme-drawer')) {
          customElements.upgrade(drawer);
        }
        if (typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
      }
      // Fallback navigation is only allowed after an explicit cart click.
      window.location.href = Theme.routes.cart_url || '/cart';
    },
  });
}

// Custom collection cards use plain forms, outside Horizon's product-form-component.
// Intercept only those forms; native product forms keep their own AJAX handler.
let collectionCartQueue = Promise.resolve();
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.closest('product-form-component')) return;
  const action = new URL(form.action, window.location.origin);
  if (action.origin !== window.location.origin || !/\/cart\/add\/?$/.test(action.pathname)) return;
  if (!form.querySelector('[name="id"]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (form.dataset.shatouraAdding === 'true') return;

  const button = event.submitter || form.querySelector('[type="submit"]');
  const data = new FormData(form);
  const originalText = button?.textContent;
  const wasDisabled = button?.disabled;
  const en = document.documentElement.lang.startsWith('en');
  form.dataset.shatouraAdding = 'true';
  form.setAttribute('aria-busy', 'true');
  if (button) { button.disabled = true; button.textContent = en ? 'Adding…' : 'جاري الإضافة…'; }
  let status = form.querySelector('[data-shatoura-cart-status]');
  if (!status) {
    status = document.createElement('span');
    status.dataset.shatouraCartStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'display:block;font-size:12px;line-height:1.4;margin-top:4px;';
    form.append(status);
  }
  status.textContent = '';

  collectionCartQueue = collectionCartQueue.then(async () => {
    let added = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map(item => item.dataset.sectionId))].slice(0, 5);
      if (sections.length) data.set('sections', sections.join(','));
      data.set('sections_url', window.location.pathname);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: data,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || (en ? 'Could not add this product.' : 'تعذر إضافة المنتج. حاول مرة أخرى.'));
      added = true;
      status.textContent = en ? 'Added to cart ✓' : 'اتضاف للسلة ✓';
      const cartResponse = await fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      form.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: [{ merchandiseId: String(data.get('id')), quantity: Number(data.get('quantity')) || 1 }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, sections: result.sections,
            source: 'shatoura-collection-form', didError: false },
        }),
      }));
    } catch (error) {
      status.textContent = added
        ? (en ? 'Added. Open the cart to view your items.' : 'اتضاف للسلة. افتح السلة لمراجعة منتجاتك.')
        : (error.message || (en ? 'Connection failed. Try again.' : 'تعذر الاتصال. حاول مرة أخرى.'));
    } finally {
      delete form.dataset.shatouraAdding;
      form.removeAttribute('aria-busy');
      if (button) { button.disabled = wasDisabled; button.textContent = originalText; }
    }
  });
}, true);

if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
